import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ChatDraftType = 'single' | 'group';

@Injectable({
  providedIn: 'root',
})
export class ChatDraftService {
  private readonly STORAGE_PREFIX = 'chat_drafts_';
  private drafts: Record<string, string> = {};
  private saveTimeout: any = null;
  private currentUserId: string = '';

  public readonly draftsUpdated$ = new BehaviorSubject<Record<string, string>>({});

  constructor() {
    this.init();
  }

  private getUserId(): string {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('guestID') || 'default';
    }
    return 'default';
  }

  private getStorageKey(): string {
    const uid = this.getUserId();
    return `${this.STORAGE_PREFIX}${uid}`;
  }

  public init(): void {
    this.currentUserId = this.getUserId();
    this.loadDrafts();
  }

  /**
   * Load drafts from localStorage into memory.
   */
  public loadDrafts(): void {
    if (typeof localStorage === 'undefined') {
      this.drafts = {};
      this.draftsUpdated$.next(this.drafts);
      return;
    }
    try {
      const key = this.getStorageKey();
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        this.drafts = (typeof parsed === 'object' && parsed !== null) ? parsed : {};
      } else {
        this.drafts = {};
      }
    } catch (e) {
      console.warn('[ChatDraftService] Failed to load drafts from localStorage:', e);
      this.drafts = {};
    }
    this.draftsUpdated$.next(this.drafts);
  }

  private makeKey(type: ChatDraftType, id: string): string {
    return `${type}:${String(id || '').trim()}`;
  }

  /**
   * Get draft text for a single or group chat.
   * If single chat, checks primary id (usually peer userId) then fallback secondaryId (channelId).
   */
  public getDraft(type: ChatDraftType, id: string, secondaryId?: string): string {
    if (!id && !secondaryId) return '';
    this.checkUserScope();

    if (id) {
      const primaryKey = this.makeKey(type, id);
      if (this.drafts[primaryKey] !== undefined) {
        return this.drafts[primaryKey];
      }
    }

    if (secondaryId) {
      const secondaryKey = this.makeKey(type, secondaryId);
      if (this.drafts[secondaryKey] !== undefined) {
        return this.drafts[secondaryKey];
      }
    }

    return '';
  }

  /**
   * Set draft text for a single or group chat.
   * Clears draft if text is empty or only whitespace.
   */
  public setDraft(type: ChatDraftType, id: string, text: string, secondaryId?: string): void {
    if (!id && !secondaryId) return;
    this.checkUserScope();

    const normalizedText = (text || '');
    if (!normalizedText.trim()) {
      this.clearDraft(type, id, secondaryId);
      return;
    }

    if (id) {
      this.drafts[this.makeKey(type, id)] = normalizedText;
    }
    if (secondaryId) {
      this.drafts[this.makeKey(type, secondaryId)] = normalizedText;
    }

    this.draftsUpdated$.next(this.drafts);
    this.scheduleSave();
  }

  /**
   * Clear draft for a chat.
   */
  public clearDraft(type: ChatDraftType, id: string, secondaryId?: string): void {
    this.checkUserScope();
    let changed = false;

    if (id) {
      const key = this.makeKey(type, id);
      if (key in this.drafts) {
        delete this.drafts[key];
        changed = true;
      }
    }

    if (secondaryId) {
      const secondaryKey = this.makeKey(type, secondaryId);
      if (secondaryKey in this.drafts) {
        delete this.drafts[secondaryKey];
        changed = true;
      }
    }

    if (changed) {
      this.draftsUpdated$.next(this.drafts);
      this.scheduleSave();
    }
  }

  /**
   * Check if a draft exists for the given chat by type and id.
   */
  public hasDraft(type: ChatDraftType, id: string, secondaryId?: string): boolean {
    const draft = this.getDraft(type, id, secondaryId);
    return !!draft.trim();
  }

  /**
   * Get draft text for a chat object (single or group), resolving all common id properties.
   */
  public getDraftForChat(chat: any): string {
    if (!chat) return '';
    const isGroup = chat.chatType === 'group' || chat.type === 'group' || !!chat.groupName || (Array.isArray(chat.members) && chat.members.length > 0);
    if (isGroup) {
      const gid = chat.channelId || chat.channel_id || chat.groupId || chat._id;
      return this.getDraft('group', gid);
    }
    const uid = chat.userId || chat.otherUserId || chat.receiverId || chat._id;
    const cid = chat.channelId || chat.channel_id;
    return this.getDraft('single', uid, cid);
  }

  /**
   * Check if a chat object has non-empty draft text.
   */
  public hasDraftForChat(chat: any): boolean {
    const draft = this.getDraftForChat(chat);
    return !!draft && draft.trim().length > 0;
  }

  /**
   * Immediately write in-memory drafts to localStorage without waiting for debounce.
   */
  public flush(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.saveToStorage();
  }

  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = null;
      this.saveToStorage();
    }, 200);
  }

  private saveToStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const key = this.getStorageKey();
      const keys = Object.keys(this.drafts);
      if (keys.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(this.drafts));
      }
    } catch (e) {
      console.warn('[ChatDraftService] Failed to save drafts to localStorage:', e);
    }
  }

  private checkUserScope(): void {
    const uid = this.getUserId();
    if (uid !== this.currentUserId) {
      this.init();
    }
  }
}
